import axios from 'axios';
import { extractApiError } from '@utils/errorParsers';

const API_BASE_URL = import.meta.env.VITE_SELL_BASE_URL;
const API_TENANT_ID = import.meta.env.VITE_SELL_TENANT_ID;

export const SELL_API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    ...(API_TENANT_ID ? { 'X-Tenant-Id': API_TENANT_ID } : {}),
  },
});

// ---------------------------------------------------------------------------
// Runtime configuration (used by embedded / library consumers)
// ---------------------------------------------------------------------------

export type SellApiConfig = {
  apiBaseUrl?: string;
  tenantId?: string;
};

export const configureSellApi = ({ apiBaseUrl, tenantId }: SellApiConfig): void => {
  if (apiBaseUrl) SELL_API.defaults.baseURL = apiBaseUrl;
  if (tenantId) SELL_API.defaults.headers['X-Tenant-Id'] = tenantId;
};

/**
 * Response interceptor: catches 4xx/5xx and re-throws a structured error
 * so hooks can surface the BE error inline in the UI.
 *
 * Normalises both `{ code, message }` and `{ errorCode, errorDescription }`
 * field variants, including raw string bodies (backend may proxy JSON without
 * application/json Content-Type).  For 404 `AMS-404` on the
 * serviceQualification endpoint the hook detects `error.name === 'AMS-404'`
 * and routes to Lead Capture instead of showing error UI.
 */
SELL_API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const parsed = extractApiError(error.response?.data);
    if (parsed) {
      error.name = parsed.code;
      error.message = parsed.message ?? error.message;
    }
    return Promise.reject(error);
  }
);
